import React from 'react';
import { useSearchParams } from 'react-router-dom';
import {
  AgnesButton,
  AgnesCard,
  AgnesCardContent,
  AgnesDataTable,
  type AgnesDataTableColumn,
  AgnesIconButton,
  AgnesPageHeader,
} from '@agnes/ui';
import { Eye, Receipt, RefreshCw } from 'lucide-react';
import MENU_PERMISSIONS from '@/constants/ConstantMenuRoles';
import { usePodeEditar } from '@/hooks/usePodeEditar';
import { DctfEmpregadorSelect } from '@/components/encargos/DctfEmpregadorSelect';
import {
  DctfMultiSelectFilter,
  type DctfFilterGroup,
} from '@/components/encargos/DctfMultiSelectFilter';
import { TableWrapper } from '@/components/table-wrapper/TableWrapper';
import DctfCollectionModal from '@/pages/encargos/DctfCollectionModal';
import DctfDetails from '@/pages/encargos/DctfDetails';
import { useDctfStore } from '@/stores/useDctfStore';
import { useEmpregadoresStore } from '@/stores/useEmpregadoresStore';
import UtilsMask from '@/utils/UtilsMask';
import { resolveAssignedEmployerCnpj } from '@/utils/assignedEmployer';
import { employerListMessage } from '@/utils/employerListMessage';
import { EmployerListRetryButton } from '@/components/EmployerListRetryButton';
import { moedaBR } from '@/utils/locale';

type Any = any;
const NO_EMPREGADORES: Any[] = [];

interface Declaracao {
  [k: string]: unknown;
  id: string;
  empresaRazaoSocial: string;
  empresaCnpj: string;
  periodoApur: string;
  debitoApurado: string | number;
  deducoes: string | number;
  outrosCreditos: string | number;
  pagamentos: string | number;
  saldoAPagar: string | number;
}

const mesmoArray = (a: string[], b: string[]) =>
  a.length === b.length && a.every((value, index) => value === b[index]);

function montarGruposPeriodos(periodos: string[]): DctfFilterGroup[] {
  const grupos = new Map<string, DctfFilterGroup>();
  periodos.forEach((periodo) => {
    const ano = periodo.split('/')[1];
    if (!ano) return;
    const grupo = grupos.get(ano) ?? { key: ano, label: ano, options: [] };
    grupo.options.push({ value: periodo, label: periodo });
    grupos.set(ano, grupo);
  });
  return [...grupos.values()];
}

export default function PageDctf() {
  const [searchParams] = useSearchParams();
  const clienteUrl = searchParams.get('cliente')?.trim() ?? '';
  const [clienteValidado, setClienteValidado] = React.useState<string | null>(null);
  const [tentativaLista, setTentativaLista] = React.useState(0);
  const [empregadoresSelecionados, setEmpregadoresSelecionados] = React.useState<string[]>([]);
  const [periodosSelecionados, setPeriodosSelecionados] = React.useState<string[]>([]);
  const [situacaoSelecionada, setSituacaoSelecionada] = React.useState('');
  const [possuiDcompSelecionado, setPossuiDcompSelecionado] = React.useState('');
  const [selectedDctf, setSelectedDctf] = React.useState<Any | null>(null);
  const [collectionOpen, setCollectionOpen] = React.useState(false);
  const detailsContainer = React.useRef<HTMLDivElement | null>(null);
  const [filtrosAplicados, setFiltrosAplicados] = React.useState({
    empregadores: [] as string[],
    periodos: [] as string[],
    situacao: '',
    possuiDcomp: '',
  });

  const lista = useDctfStore((s) => s.lista);
  const total = useDctfStore((s) => s.total);
  const cnpjsAplicados = useDctfStore((s) => s.cnpjs);
  const periodosDisponiveis = useDctfStore((s) => s.periodosDisponiveis);
  const carregando = useDctfStore((s) => s.carregando);
  const carregandoPeriodos = useDctfStore((s) => s.carregandoPeriodos);
  const page = useDctfStore((s) => s.page);
  const size = useDctfStore((s) => s.size);
  const sortBy = useDctfStore((s) => s.sortBy);
  const sortOrder = useDctfStore((s) => s.sortOrder);
  const setSort = useDctfStore((s) => s.setSort);
  const canUpdateDctf = usePodeEditar(MENU_PERMISSIONS.DCTF_EDITAR);
  const listaEmpregadores = useEmpregadoresStore((s) => s.lista);
  const carregandoEmpregadores = useEmpregadoresStore((s) => s.carregando);
  const empregadoresCarregados = useEmpregadoresStore((s) => s.carregado);
  const erroEmpregadores = useEmpregadoresStore((s) => s.erro);
  const validacaoPronta = clienteValidado === clienteUrl;
  const empregadores = validacaoPronta ? listaEmpregadores : NO_EMPREGADORES;
  const cnpjsPermitidos = empregadores
    .map((empregador: Any) => empregador.cnpjFilial)
    .filter(Boolean);
  const cnpjsPermitidosSet = new Set(cnpjsPermitidos);
  const clienteUrlForaDaLista = Boolean(clienteUrl && !cnpjsPermitidosSet.has(clienteUrl));
  const gruposPeriodos = React.useMemo(
    () => montarGruposPeriodos(periodosDisponiveis),
    [periodosDisponiveis],
  );
  const listaAcessivel =
    validacaoPronta && (!clienteUrl || cnpjsAplicados.some((cnpj) => cnpjsPermitidosSet.has(cnpj)));
  const listaVisivel = listaAcessivel ? lista : [];
  const totalVisivel = listaAcessivel ? total : 0;
  const dirty =
    !mesmoArray(empregadoresSelecionados, filtrosAplicados.empregadores) ||
    !mesmoArray(periodosSelecionados, filtrosAplicados.periodos) ||
    situacaoSelecionada !== filtrosAplicados.situacao ||
    possuiDcompSelecionado !== filtrosAplicados.possuiDcomp;
  const temFiltros =
    empregadoresSelecionados.length > 0 ||
    periodosSelecionados.length > 0 ||
    situacaoSelecionada !== '' ||
    possuiDcompSelecionado !== '' ||
    filtrosAplicados.empregadores.length > 0 ||
    filtrosAplicados.periodos.length > 0 ||
    filtrosAplicados.situacao !== '' ||
    filtrosAplicados.possuiDcomp !== '';
  const noOptionsText = employerListMessage({
    loading: carregandoEmpregadores || !validacaoPronta,
    loaded: validacaoPronta && empregadoresCarregados,
    error: erroEmpregadores,
  });

  React.useEffect(() => {
    let atual = true;
    setClienteValidado(null);
    useDctfStore.getState().limparLista();
    void useEmpregadoresStore
      .getState()
      .buscar()
      .then(async () => {
        if (!atual) return;
        const { lista: empresasAtuais, carregado } = useEmpregadoresStore.getState();
        const cnpjUrlPermitido =
          clienteUrl && carregado ? resolveAssignedEmployerCnpj(clienteUrl, empresasAtuais) : '';
        const cnpjs = carregado
          ? cnpjUrlPermitido
            ? [cnpjUrlPermitido]
            : clienteUrl
              ? []
              : empresasAtuais.map((empresa: Any) => empresa.cnpjFilial).filter(Boolean)
          : [];
        const selecaoInicial = cnpjUrlPermitido ? [cnpjUrlPermitido] : [];
        setEmpregadoresSelecionados(selecaoInicial);
        setPeriodosSelecionados([]);
        setSituacaoSelecionada('');
        setPossuiDcompSelecionado('');
        setFiltrosAplicados({
          empregadores: selecaoInicial,
          periodos: [],
          situacao: '',
          possuiDcomp: '',
        });
        await useDctfStore.getState().inicializar(cnpjs);
        if (atual) setClienteValidado(clienteUrl);
      });
    return () => {
      atual = false;
    };
  }, [clienteUrl, tentativaLista]);

  const tentarCarregarEmpregadoresNovamente = () => {
    setClienteValidado(null);
    setTentativaLista((tentativa) => tentativa + 1);
  };

  const onEmpregadoresChange = (novosCnpjs: string[]) => {
    const validos = novosCnpjs.filter((cnpj) => cnpjsPermitidosSet.has(cnpj));
    setEmpregadoresSelecionados(validos);
    setPeriodosSelecionados([]);
    void useDctfStore.getState().carregarPeriodos(validos);
  };

  const onAplicar = async () => {
    const selecionados = empregadoresSelecionados.filter((cnpj) => cnpjsPermitidosSet.has(cnpj));
    const cnpjs =
      selecionados.length > 0 ? selecionados : clienteUrlForaDaLista ? [] : cnpjsPermitidos;
    setFiltrosAplicados({
      empregadores: empregadoresSelecionados,
      periodos: periodosSelecionados,
      situacao: situacaoSelecionada,
      possuiDcomp: possuiDcompSelecionado,
    });
    await useDctfStore
      .getState()
      .aplicarFiltros(
        cnpjs,
        periodosSelecionados,
        situacaoSelecionada === '' ? undefined : situacaoSelecionada === 'true',
        possuiDcompSelecionado === '' ? undefined : possuiDcompSelecionado === 'true',
      );
  };

  const onLimpar = async () => {
    setEmpregadoresSelecionados([]);
    setPeriodosSelecionados([]);
    setSituacaoSelecionada('');
    setPossuiDcompSelecionado('');
    setFiltrosAplicados({ empregadores: [], periodos: [], situacao: '', possuiDcomp: '' });
    const cnpjs = clienteUrlForaDaLista ? [] : cnpjsPermitidos;
    await Promise.all([
      useDctfStore.getState().carregarPeriodos(cnpjs),
      useDctfStore.getState().aplicarFiltros(cnpjs, [], undefined, undefined),
    ]);
  };

  const onCollectionExecuted = async (cnpjs: string[]) => {
    setCollectionOpen(false);
    const { cnpjs: cnpjsAtuais } = useDctfStore.getState();
    if (cnpjs.some((cnpj) => cnpjsAtuais.includes(cnpj))) {
      await useDctfStore.getState().carregarPeriodos(cnpjsAtuais);
      const periodosAtuais = useDctfStore.getState().periodosDisponiveis;
      setPeriodosSelecionados(periodosAtuais);
      setFiltrosAplicados((filtros) => ({ ...filtros, periodos: periodosAtuais }));
      await useDctfStore
        .getState()
        .aplicarFiltros(
          cnpjsAtuais,
          periodosAtuais,
          filtrosAplicados.situacao === '' ? undefined : filtrosAplicados.situacao === 'true',
          filtrosAplicados.possuiDcomp === '' ? undefined : filtrosAplicados.possuiDcomp === 'true',
        );
    }
  };

  const columns: AgnesDataTableColumn<Declaracao>[] = [
    {
      key: 'empresaRazaoSocial',
      header: 'Empregador',
      width: 300,
      sortable: true,
      render: (row) => (
        <div>
          <div>{row.empresaRazaoSocial}</div>
          <div className="text-xs text-agnes-text-muted">{UtilsMask.cnpj(row.empresaCnpj)}</div>
        </div>
      ),
    },
    { key: 'periodoApur', header: 'Período de apuração', width: 160, sortable: true },
    {
      key: 'debitoApurado',
      header: 'Débito apurado',
      width: 150,
      sortable: true,
      render: (row) => <span className="text-red-700">{moedaBR(row.debitoApurado)}</span>,
    },
    {
      key: 'deducoes',
      header: 'Deduções',
      width: 130,
      sortable: true,
      render: (row) => <span className="text-green-700">{moedaBR(row.deducoes)}</span>,
    },
    {
      key: 'outrosCreditos',
      header: 'Outros créditos',
      width: 150,
      sortable: true,
      render: (row) => <span className="text-green-700">{moedaBR(row.outrosCreditos)}</span>,
    },
    {
      key: 'pagamentos',
      header: 'Pagamentos',
      width: 140,
      sortable: true,
      render: (row) => <span className="text-green-700">{moedaBR(row.pagamentos)}</span>,
    },
    {
      key: 'saldoAPagar',
      header: 'Saldo a pagar',
      width: 150,
      sortable: true,
      render: (row) => moedaBR(row.saldoAPagar),
    },
    {
      key: 'acoes',
      header: 'Ações',
      width: 90,
      align: 'center',
      toggleable: false,
      render: (row) => (
        <AgnesIconButton aria-label="Ver detalhes" size="sm" onClick={() => setSelectedDctf(row)}>
          <Eye className="h-4 w-4" />
        </AgnesIconButton>
      ),
    },
  ];

  const breadcrumbs = [
    { label: 'Home', href: '/home' },
    { label: 'Encargos' },
    {
      label: 'Débitos e Créditos Tributários',
      ...(selectedDctf ? { href: '/encargos/dctf' } : {}),
    },
    ...(selectedDctf
      ? [{ label: `${UtilsMask.cnpj(selectedDctf.empresaCnpj)} (${selectedDctf.periodoApur})` }]
      : []),
  ];

  React.useEffect(() => {
    if (selectedDctf && detailsContainer.current?.scrollIntoView) {
      detailsContainer.current.scrollIntoView({ behavior: 'smooth', block: 'start' });
    }
  }, [selectedDctf]);

  return (
    <>
      <AgnesPageHeader
        icon={<Receipt />}
        title="Débitos e Créditos Tributários"
        subtitle="Monitore os débitos das contribuições previdenciárias e de contribuições destinadas a terceiros pagas ou devidas e possíveis créditos"
        breadcrumbs={breadcrumbs}
      />

      <AgnesCard className="w-full">
        <AgnesCardContent className="flex w-full flex-col items-stretch gap-4">
          {selectedDctf ? (
            <div ref={detailsContainer}>
              <AgnesButton variant="text" onClick={() => setSelectedDctf(null)}>
                Voltar a página anterior
              </AgnesButton>
              <div className="mt-4">
                <DctfDetails key={selectedDctf.id} dctf={selectedDctf} />
              </div>
            </div>
          ) : (
            <>
              <div className="flex w-full flex-col items-end gap-4 xl:flex-row">
                <DctfEmpregadorSelect
                  label="Empregador"
                  placeholder="Todos"
                  empregadores={empregadores}
                  value={empregadoresSelecionados}
                  disabled={!validacaoPronta || carregandoEmpregadores}
                  noOptionsText={noOptionsText}
                  className="min-w-[15rem] flex-1"
                  onChange={onEmpregadoresChange}
                />
                <DctfMultiSelectFilter
                  label="Período"
                  placeholder="Todos"
                  groups={gruposPeriodos}
                  value={periodosSelecionados}
                  disabled={
                    !validacaoPronta ||
                    carregandoPeriodos ||
                    empregadoresSelecionados.length === 0 ||
                    carregandoEmpregadores
                  }
                  emptyText="Nenhum período de apuração disponível"
                  onChange={setPeriodosSelecionados}
                />
                <label className="min-w-[12rem] flex-1">
                  <span className="mb-1.5 block text-sm font-medium leading-none text-agnes-text">
                    Situação
                  </span>
                  <select
                    aria-label="Situação"
                    value={situacaoSelecionada}
                    onChange={(event) => setSituacaoSelecionada(event.target.value)}
                    disabled={!validacaoPronta || carregandoEmpregadores}
                    className="h-12 w-full rounded-md border border-agnes-border bg-agnes-bg-surface px-3 text-sm text-agnes-text disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Todas</option>
                    <option value="true">Zerada</option>
                    <option value="false">Com débito</option>
                  </select>
                </label>
                <label className="min-w-[12rem] flex-1">
                  <span className="mb-1.5 block text-sm font-medium leading-none text-agnes-text">
                    Possui DCOMP
                  </span>
                  <select
                    aria-label="Possui DCOMP"
                    value={possuiDcompSelecionado}
                    onChange={(event) => setPossuiDcompSelecionado(event.target.value)}
                    disabled={!validacaoPronta || carregandoEmpregadores}
                    className="h-12 w-full rounded-md border border-agnes-border bg-agnes-bg-surface px-3 text-sm text-agnes-text disabled:cursor-not-allowed disabled:opacity-50"
                  >
                    <option value="">Todos</option>
                    <option value="true">Sim</option>
                    <option value="false">Não</option>
                  </select>
                </label>
                <div className="flex shrink-0 gap-2">
                  <AgnesButton
                    variant="primary"
                    disabled={
                      !validacaoPronta || carregando || !dirty || cnpjsPermitidos.length === 0
                    }
                    onClick={() => void onAplicar()}
                  >
                    Aplicar
                  </AgnesButton>
                  <AgnesButton
                    variant="secondary"
                    disabled={!temFiltros || carregando}
                    onClick={() => void onLimpar()}
                  >
                    Limpar
                  </AgnesButton>
                  {canUpdateDctf &&
                    validacaoPronta &&
                    empregadoresCarregados &&
                    empregadores.length > 0 && (
                      <AgnesButton
                        variant="secondary"
                        startIcon={<RefreshCw className="h-4 w-4" />}
                        onClick={() => setCollectionOpen(true)}
                      >
                        Sincronizar
                      </AgnesButton>
                    )}
                </div>
              </div>

              <EmployerListRetryButton
                error={erroEmpregadores}
                onRetry={tentarCarregarEmpregadoresNovamente}
              />
              {validacaoPronta &&
                empregadoresCarregados &&
                empregadores.length === 0 &&
                !erroEmpregadores && (
                  <p role="status" className="text-sm text-agnes-text-muted">
                    {noOptionsText}
                  </p>
                )}

              <TableWrapper className="w-full">
                <AgnesDataTable<Declaracao>
                  columns={columns}
                  data={listaVisivel}
                  rowKey={(row) => String(row.id)}
                  loading={!validacaoPronta || carregando}
                  sort={{ sortBy, sortOrder, onSortChange: setSort }}
                  pagination={{
                    page,
                    pageSize: size,
                    total: totalVisivel,
                    serverSide: true,
                    onPageChange: (novoPage: number) =>
                      void useDctfStore.getState().setPage(novoPage),
                    onPageSizeChange: (novoSize: number) =>
                      void useDctfStore.getState().setSize(novoSize),
                  }}
                />
              </TableWrapper>
            </>
          )}
        </AgnesCardContent>
      </AgnesCard>
      <DctfCollectionModal
        open={collectionOpen}
        employers={validacaoPronta ? empregadores : NO_EMPREGADORES}
        onCancel={() => setCollectionOpen(false)}
        onExecuted={(cnpjs) => void onCollectionExecuted(cnpjs)}
      />
    </>
  );
}
